import { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { Modal, useMultiStepModal } from '@alltrails/modal';
import { UserConnectionActionType } from '@alltrails/types/belay';
import SuccessToast from '@alltrails/shared/types/successToast';
import logContentReported from '@alltrails/amplitude/events/logContentReported';
import ReportContentType from '@alltrails/amplitude/enums/ReportContentType';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import useUserConnection from '@alltrails/modules/UserConnections/useUserConnection';
import useConnectAction from '@alltrails/modules/UserConnections/useConnectAction';
import ReportIssue from '../ReportIssue/ReportIssue';
import ReportDrilldown from '../ReportDrilldown/ReportDrilldown';
import { EntryPoint, Issue, Report, ReportedUser, ReportType } from '../../types';
import { handleReport, analyticsReportReason } from '../../utils/reportUtils';

type ReportIssueModalProps = {
  currentUserId: number;
  entryPoint: EntryPoint;
  isOpen: boolean;
  reportId: string;
  trailReviewId?: string;
  commentId?: string;
  reportType: ReportType;
  reportUser: ReportedUser;
  onCloseRequest: () => void;
  setSuccessToast: Dispatch<SetStateAction<SuccessToast>>;
  analyticsData?: { location: ReportLocation; contentType: ReportContentType };
};

const messages = defineMessages({
  title: { defaultMessage: 'Report an issue' },
  subtext: { defaultMessage: 'What would you like to report?' },
  otherSubtext: { defaultMessage: 'Please explain your reason for reporting.' },
  spamLabel: { defaultMessage: 'Spam' },
  spamDesc: { defaultMessage: 'This might include unwanted solicitations, advertising or promotions, fraud or phishing.' },
  spamAccountLabel: { defaultMessage: 'Spam account' },
  spamAccountDesc: {
    defaultMessage: 'This account has a pattern of engaging in unwanted solicitations, advertising or promotions, fraud or phishing.'
  },
  fakeProfileLabel: { defaultMessage: 'Fake profile' },
  fakeProfileDesc: {
    defaultMessage: 'This account is impersonating another member or otherwise presenting themselves in a false or misleading way.'
  },
  harmfulContentLabel: { defaultMessage: 'Harmful content' },
  harmfulContentDesc: { defaultMessage: 'This might include hate speech, harassment, bullying, violence, or otherwise offensive content.' },
  harmfulBehaviorLabel: { defaultMessage: 'Harmful behavior' },
  harmfulBehaviorDesc: {
    defaultMessage: 'This account has a pattern of using or promoting hate speech, harassment, bullying, violence, or otherwise offensive content.'
  },
  privacyIssueLabel: { defaultMessage: 'Privacy issue' },
  privacyIssueDesc: { defaultMessage: 'This might include the sharing of personal information or owned imagery without permission.' },
  privacyUserIssueDesc: { defaultMessage: 'This account has a pattern of sharing of personal information or owned imagery without permission.' },
  otherLabel: { defaultMessage: 'Other' },
  saveError: { defaultMessage: 'Something went wrong. Please try again.' },
  submitReport: { defaultMessage: 'Submit report' },
  successText: { defaultMessage: 'Thanks for reporting. We appreciate you letting  us know.' },
  successTextBlock: { defaultMessage: 'Thanks for reporting. We appreciate you letting us know. You’ll no longer see this user’s information.' }
});

export default function ReportIssueModal({
  currentUserId,
  entryPoint,
  isOpen,
  reportId,
  trailReviewId,
  commentId,
  reportType,
  reportUser,
  onCloseRequest,
  setSuccessToast,
  analyticsData
}: ReportIssueModalProps) {
  const { activeModal, goBack, navigateTo } = useMultiStepModal<'reportIssue' | 'drilldown'>('reportIssue');
  const {
    formattedDefaultMessages: {
      title,
      subtext,
      otherSubtext,
      spamLabel,
      spamDesc,
      spamAccountLabel,
      spamAccountDesc,
      fakeProfileLabel,
      fakeProfileDesc,
      harmfulContentLabel,
      harmfulContentDesc,
      harmfulBehaviorLabel,
      harmfulBehaviorDesc,
      privacyIssueLabel,
      privacyIssueDesc,
      privacyUserIssueDesc,
      otherLabel,
      submitReport,
      successText,
      successTextBlock
    }
  } = useFormatMessage(messages);
  const [selectedReport, setSelectedReport] = useState<Report | undefined>(undefined);
  const [isBlockSelected, setIsBlockSelected] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorToastVisible, setIsErrorToastVisible] = useState<boolean>(false);
  const userConnection = useUserConnection(currentUserId, reportUser.id as number);
  const blockAction = useConnectAction(userConnection, UserConnectionActionType.Block);
  // Check if we have a real user (not a dummy user with empty username)
  const hasRealUser = ('userName' in reportUser && reportUser.userName !== '') || ('firstName' in reportUser && reportUser.firstName !== '');

  useEffect(() => {
    if (errorToastVisible) {
      setTimeout(() => {
        setIsErrorToastVisible(false);
      }, 3000);
    }
  }, [errorToastVisible]);

  const onReportSelect = (report: Report) => {
    setSelectedReport(report);
    navigateTo('drilldown');
  };

  const reports: Report[] = useMemo(() => {
    const options: Report[] = [];
    if (!reportType) {
      return [];
    }
    const generalOptions: Report[] = [
      {
        id: reportType === 'profile' ? Issue.SpamAccount : Issue.Spam,
        label: reportType === 'profile' ? spamAccountLabel : spamLabel,
        description: reportType === 'profile' ? spamAccountDesc : spamDesc
      },
      {
        id: reportType === 'profile' ? Issue.HarmfulBehavior : Issue.HarmfulContent,
        label: reportType === 'profile' ? harmfulBehaviorLabel : harmfulContentLabel,
        description: reportType === 'profile' ? harmfulBehaviorDesc : harmfulContentDesc
      },
      { id: Issue.PrivacyIssue, label: privacyIssueLabel, description: reportType === 'profile' ? privacyUserIssueDesc : privacyIssueDesc },
      { id: Issue.Other, label: otherLabel }
    ];
    options.push(...generalOptions);
    if (reportType === 'profile') {
      options.unshift({ id: Issue.FakeProfile, label: fakeProfileLabel, description: fakeProfileDesc });
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const reasonText = useMemo(() => {
    if (!selectedReport) {
      return undefined;
    }
    if (selectedReport.id === Issue.Other) {
      return otherReason;
    }
    return selectedReport.id;
  }, [selectedReport, otherReason]);

  const logAnalytics = useCallback(() => {
    if (selectedReport && analyticsData) {
      logContentReported({
        block_user: isBlockSelected,
        content_id: reportId,
        content_type: analyticsData.contentType,
        other_text_reason: otherReason,
        owner_id: String(reportUser.id),
        report_location: analyticsData.location,
        report_reason: analyticsReportReason(selectedReport.id)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  const onReportSubmit = useCallback(async () => {
    if (!reasonText) {
      return;
    }
    setIsLoading(true);
    try {
      logAnalytics();
      await handleReport(
        entryPoint,
        reportId,
        reportUser,
        trailReviewId,
        commentId,
        reasonText,
        isBlockSelected,
        setIsErrorToastVisible,
        setIsLoading,
        successTextBlock,
        successText,
        setSuccessToast,
        onCloseRequest
      );
    } catch {
      setIsErrorToastVisible(true);
    }
  }, [
    entryPoint,
    reportId,
    reportUser,
    trailReviewId,
    commentId,
    reasonText,
    isBlockSelected,
    successText,
    successTextBlock,
    setSuccessToast,
    onCloseRequest,
    logAnalytics
  ]);

  const subtitle = useMemo(() => {
    if (activeModal === 'reportIssue') {
      return subtext;
    }
    return selectedReport?.id === Issue.Other ? otherSubtext : subtext;
  }, [activeModal, otherSubtext, selectedReport?.id, subtext]);

  return (
    <Modal
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      onBackRequest={goBack}
      title={title}
      subtitle={subtitle}
      buttons={
        activeModal === 'drilldown'
          ? {
              primaryButton: {
                text: submitReport,
                onClick: () => {
                  // Only call blockAction if we have a real user (not a dummy user with empty username)
                  if (isBlockSelected && blockAction && hasRealUser) {
                    blockAction();
                  }
                  onReportSubmit();
                },
                loading: isLoading,
                testId: 'submit-report-btn'
              }
            }
          : undefined
      }
    >
      {activeModal === 'reportIssue' ? (
        <ReportIssue reports={reports} onReportSelect={onReportSelect} />
      ) : (
        <ReportDrilldown
          report={selectedReport}
          reportUser={reportUser}
          isBlockSelected={isBlockSelected}
          setIsBlockSelected={setIsBlockSelected}
          otherReason={otherReason}
          setOtherReason={setOtherReason}
          errorToastVisible={errorToastVisible}
          hasRealUser={!!hasRealUser}
        />
      )}
    </Modal>
  );
}
